import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import FileUploadSection from './components/FileUploadSection';
import MarkingResults from './components/MarkingResults';
import { uploadForMarkingDirect, validateFile, AutoMarkingResponse } from '../../services/autoMarking';
import './AutoMarking.css';

const AutoMarkingPage: React.FC = () => {
  const canAccess = useSelector((state: any) => state.auth.permissions?.opsmanual);
  
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [markingResults, setMarkingResults] = useState<AutoMarkingResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeResultIndex, setActiveResultIndex] = useState<number | null>(null);

  if (!canAccess?.list) {
    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              You do not have permission to access Auto Marking.
            </div>
          </div>
        </section>
      </div>
    );
  }

  const handleFileUpload = async (files: File[]) => {
    if (!canAccess?.['upload-document']) {
      toast.error('You do not have permission to upload files for marking');
      return;
    }

    // Validate all files first
    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast.error(`${file.name}: ${validation.error}`);
        return;
      }
    }

    setLoading(true);
    setUploadedFiles(files);
    const results: AutoMarkingResponse[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        toast.info(`Processing ${file.name}... (${i + 1}/${files.length})`);
        
        const response = await uploadForMarkingDirect(file);
        
        console.log('Upload response:', response);
        console.log('Response success:', response.success);
        console.log('Response data:', response.data);
        
        if (response.success && response.data) {
          // Create image URL for preview
          const imageUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
          
          const resultData = {
            fileName: file.name,
            fileSize: file.size,
            uploadTime: new Date().toISOString(),
            imageUrl: imageUrl,
            imageFile: file,
            success: true,
            data: response.data, // Keep data nested under 'data' property
            summary: response.data.summary // Also add summary at top level for sidebar display
          };
          
          console.log('Result data being pushed:', resultData);
          
          results.push(resultData);
          toast.success(`${file.name} processed successfully`);
        } else {
          toast.error(`Failed to process ${file.name}: ${response.message}`);
          results.push({
            fileName: file.name,
            fileSize: file.size,
            uploadTime: new Date().toISOString(),
            error: response.message || 'Processing failed',
            success: false
          });
        }
      }
      
      setMarkingResults(prev => [...prev, ...results]);
      if (results.length > 0) {
        setActiveResultIndex(markingResults.length); // Set to first new result
      }
      
      // Clear uploaded files list after processing
      setUploadedFiles([]);
      
      // Return success to inform FileUploadSection to clear selected files
      return true;
    } catch (error) {
      toast.error('Failed to process files');
      console.error('Auto marking error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleClearResults = () => {
    setMarkingResults([]);
    setUploadedFiles([]);
    setActiveResultIndex(null);
  };

  const handleResultSelect = (index: number) => {
    setActiveResultIndex(index);
  };

  return (
    <div className="auto-marking-page">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Auto Marking</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item active">Auto Marking</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">

          {/* ── Beta warning banner ── */}
          <div className="alert alert-warning d-flex align-items-start mb-3" style={{ borderLeft: '4px solid #f59e0b', borderRadius: 6 }}>
            <i className="fas fa-flask mt-1 mr-2" style={{ color: '#d97706', fontSize: '1rem', flexShrink: 0 }} />
            <div>
              <strong>Beta Feature</strong> — AI marking is still being refined and may make mistakes. Please review before recording the final mark.
            </div>
          </div>

          <div className="row">
            {/* Upload Section */}
            <div className="col-12">
              <FileUploadSection
                onFileUpload={handleFileUpload}
                loading={loading}
                uploadedFiles={uploadedFiles}
                onClearResults={handleClearResults}
                hasResults={markingResults.length > 0}
              />
            </div>

            {/* Results Section */}
            {markingResults.length > 0 && (
              <div className="col-12 mt-4">
                <MarkingResults
                  results={markingResults}
                  activeIndex={activeResultIndex}
                  onResultSelect={handleResultSelect}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AutoMarkingPage;